import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useVapi from '../hooks/useVapi';

const { FiMic, FiMicOff, FiSettings, FiMessageSquare, FiWifi, FiWifiOff } = FiIcons;

// Debug helper for component
const componentDebug = (action, data = null) => {
  const timestamp = new Date().toISOString();
  console.group(`🎯 [${timestamp}] VoiceChat - ${action}`);
  if (data) {
    console.log('Data:', data);
  }
  console.groupEnd();
};

const VoiceChat = () => {
  const [showSettings, setShowSettings] = useState(false);

  // Get configuration from environment variables
  const apiKey = import.meta.env.VITE_VAPI_API_KEY;
  const assistantId = import.meta.env.VITE_VAPI_ASSISTANT_ID;
  const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;

  // Debug component initialization
  useEffect(() => {
    componentDebug('COMPONENT_INIT', {
      hasApiKey: !!apiKey,
      hasAssistantId: !!assistantId,
      hasWebhookUrl: !!webhookUrl,
      apiKeySource: 'environment',
      assistantIdSource: 'environment',
      webhookSource: 'environment'
    });
  }, []);

  const {
    isConnected,
    isLoading,
    error,
    transcript,
    isSpeaking,
    callId,
    startCall,
    endCall,
    isMuted,
    toggleMute
  } = useVapi();

  const handleVoiceToggle = () => {
    componentDebug('VOICE_TOGGLE', {
      currentState: isConnected ? 'connected' : 'disconnected',
      action: isConnected ? 'ending call' : 'starting call',
      assistantId: isConnected ? null : assistantId,
      callId: callId
    });
    
    if (isConnected) {
      endCall();
    } else {
      startCall(assistantId);
    }
  };

  // Show error if environment variables are missing
  if (!apiKey || !assistantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiSettings} className="text-2xl text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuration Required</h1>
          <p className="text-gray-600 mb-4">
            Please set up your environment variables in the .env file:
          </p>
          <div className="bg-gray-100 rounded-lg p-4 text-left text-sm font-mono">
            <div className="mb-2">
              <span className={!apiKey ? 'text-red-600' : 'text-green-600'}>
                VITE_VAPI_API_KEY={!apiKey ? 'missing' : 'configured'}
              </span>
            </div>
            <div className="mb-2">
              <span className={!assistantId ? 'text-red-600' : 'text-green-600'}>
                VITE_VAPI_ASSISTANT_ID={!assistantId ? 'missing' : 'configured'}
              </span>
            </div>
            <div>
              <span className={!webhookUrl ? 'text-red-600' : 'text-green-600'}>
                VITE_N8N_WEBHOOK_URL={!webhookUrl ? 'missing' : 'configured'}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Restart the development server after updating the .env file.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiMessageSquare} className="text-xl text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">AI Dave</h1>
              <div className="flex items-center space-x-2 text-sm">
                <SafeIcon 
                  icon={isConnected ? FiWifi : FiWifiOff} 
                  className={`text-sm ${isConnected ? 'text-green-500' : 'text-gray-400'}`} 
                />
                <span className={isConnected ? 'text-green-600' : 'text-gray-500'}>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="text-xs text-gray-500">
              Environment: {apiKey ? '✓' : '✗'} API | {assistantId ? '✓' : '✗'} Assistant | {webhookUrl ? '✓' : '✗'} Webhook
              {callId && (
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-mono">
                  Call ID: {callId.substring(0, 8)}...
                </span>
              )}
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiSettings} className="text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl text-center">
          {/* Voice Button */}
          <motion.div
            className="relative mb-8"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={handleVoiceToggle}
                disabled={isLoading}
                className={`w-32 h-32 rounded-full flex items-center justify-center text-4xl text-white transition-all duration-300 shadow-lg ${
                  isConnected
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-blue-500 hover:bg-blue-600'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <SafeIcon icon={isConnected ? FiMicOff : FiMic} />
              </button>

              {/* Mute Button */}
              {isConnected && (
                <div className="flex flex-col items-center justify-center">
                  <button
                    onClick={toggleMute}
                    className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl text-white transition-all duration-300 shadow-lg ${
                      isMuted ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-500 hover:bg-gray-600'
                    }`}
                  >
                    <SafeIcon icon={isMuted ? FiMicOff : FiMic} />
                  </button>
                  <p className={`mt-2 text-sm ${isMuted ? 'text-yellow-600' : 'text-gray-600'}`}>
                    {isMuted ? 'Mic Muted' : 'Mic Active'}
                  </p>
                </div>
              )}
            </div>

            {/* Speaking Animation */}
            <AnimatePresence>
              {isSpeaking && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute inset-0 rounded-full border-4 border-blue-300 pointer-events-none"
                >
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="w-full h-full rounded-full border-4 border-blue-200"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Status Text */}
          <div className="mb-6">
            {isLoading && (
              <p className="text-gray-600">Connecting...</p>
            )}
            {isConnected && !isSpeaking && (
              <p className="text-blue-600">Listening... Tap to stop</p>
            )}
            {isSpeaking && (
              <p className="text-green-600">Speaking...</p>
            )}
            {!isConnected && !isLoading && (
              <p className="text-gray-600">Tap to start voice chat</p>
            )}
          </div>

          {/* Transcript */}
          <AnimatePresence>
            {transcript && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 max-w-xl mx-auto"
              >
                <h3 className="text-sm font-medium text-gray-500 mb-2">Last Message</h3>
                <p className="text-gray-900">{transcript}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Display */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4 max-w-xl mx-auto"
              >
                <p className="text-red-700">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <h2 className="text-xl font-bold mb-4">Environment Configuration</h2>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Current Configuration</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>VAPI API Key:</span>
                      <span className={apiKey ? 'text-green-600' : 'text-red-600'}>
                        {apiKey ? 'Configured' : 'Missing'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Assistant ID:</span>
                      <span className={assistantId ? 'text-green-600' : 'text-red-600'}>
                        {assistantId ? 'Configured' : 'Missing'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>N8N Webhook URL:</span>
                      <span className={webhookUrl ? 'text-green-600' : 'text-red-600'}>
                        {webhookUrl ? 'Configured' : 'Missing'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Call Debug Information */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-2">Call Debug Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Connection Status:</span>
                      <span className={isConnected ? 'text-green-600' : 'text-gray-600'}>
                        {isConnected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Call ID:</span>
                      <span className={callId ? 'text-green-600 font-mono text-xs' : 'text-gray-400'}>
                        {callId || 'No active call'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Speaking Status:</span>
                      <span className={isSpeaking ? 'text-green-600' : 'text-gray-600'}>
                        {isSpeaking ? 'Speaking' : 'Silent'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Loading Status:</span>
                      <span className={isLoading ? 'text-yellow-600' : 'text-gray-600'}>
                        {isLoading ? 'Loading...' : 'Ready'}
                      </span>
                    </div>
                    {error && (
                      <div className="flex justify-between">
                        <span>Error:</span>
                        <span className="text-red-600 text-xs">
                          {error}
                        </span>
                      </div>
                    )}
                  </div>
                  {callId && (
                    <div className="mt-3 p-2 bg-white rounded border">
                      <p className="text-xs text-gray-600 mb-1">Full Call ID:</p>
                      <p className="font-mono text-xs text-gray-800 break-all">{callId}</p>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-2">How to Update</h3>
                  <p className="text-sm text-blue-700 mb-2">
                    Edit the .env file in your project root:
                  </p>
                  <div className="bg-white rounded border p-2 text-xs font-mono">
                    VITE_VAPI_API_KEY=your_api_key<br/>
                    VITE_VAPI_ASSISTANT_ID=your_assistant_id<br/>
                    VITE_N8N_WEBHOOK_URL=your_webhook_url
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Restart the dev server after making changes.
                  </p>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceChat;